import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RobotPartDto } from '@bhops/shared';
import { canEditEcn, canSwapRobotParts } from '@/permissions';
import { partsApi } from '@/api/parts';
import { robotEcnsApi } from '@/api/robot-ecns';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { EcnApplyBoard } from './EcnApplyBoard';
import { PartInstallBoard } from './PartInstallBoard';
import { PartRemoveModal } from './PartRemoveModal';
import { FoldSection } from './robot-board';
import { HistoryList, Sheet, SheetEmpty, SheetHead, type HistoryItem } from './robot-detail-ui';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.4 — Robot Detail "부품" 탭.
//
// 위: 부품 장착 보드(PartInstallBoard, 기본 접힘) — 부품 목록 · 로봇 그림 · 현재 장착 부품 (removedAt IS NULL).
// 가운데: 적용 변경점 보드(EcnApplyBoard, 기본 펼침) — 같은 모양.
// 아래: 교체 이력 (모든 row, 최신순). 2026-11-02 — 개요의 최근 이력과 같은 목록 모양.

const REMOVAL_REASON_LABEL: Record<string, string> = {
  WEAR: '마모',
  DEFECT: '결함',
  UPGRADE: '업그레이드',
  RECALL: '리콜',
};

// 이력 줄에 영문 코드(SUSPECTED)가 그대로 보이던 것을 사람의 말로(2026-11-02).
const LOT_DEFECT_LABEL: Record<string, string> = {
  SUSPECTED: '결함 의심',
  CONFIRMED: '결함 확정',
  CLEARED: '결함 해제',
};

export function RobotPartsTab({ robotId }: { robotId: string }) {
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구1 ⓓ — 종전엔 canEdit 하나(ADMIN/MANAGER/FIELD)로
  // 두 섹션을 같이 게이트했는데, 서버 규칙이 **둘**이라 한 변수로는 반드시
  // 어느 한쪽과 어긋난다(제조팀은 버튼이 없고, 필드팀은 ECN 버튼이 보이는데
  // 누르면 403). 서버 규칙별로 게이트를 쪼갠다:
  //   부품 장착·제거  ↔ robot-parts.controller @Roles = canSwapRobotParts
  //   변경점(ECN) 태깅 ↔ robot-ecn.service WRITE_ROLES = canEditEcn
  const canSwapParts = role != null && canSwapRobotParts(role);
  const canTagEcn = role != null && canEditEcn(role);
  const [removingPart, setRemovingPart] = useState<RobotPartDto | null>(null);

  // 접힌 머리의 개수 — 보드 안과 같은 queryKey 라 네트워크는 한 번이다.
  const ecnsQ = useQuery({
    queryKey: ['robot-ecns', robotId],
    queryFn: () => robotEcnsApi.list(robotId),
    staleTime: 30_000,
  });
  const ecnCount = ecnsQ.data?.items.length ?? 0;

  const q = useQuery({
    queryKey: ['robot-parts', robotId],
    queryFn: () => partsApi.listForRobot(robotId),
    staleTime: 30_000,
  });

  // 현재 장착 (removedAt IS NULL) vs 이력.
  // 로더(LOADER)는 전용 '로더' 탭에서 관리 — 이중 표현을 막기 위해 이 그리드에선 제외
  // (docs/plan/loader-history-traceability-2026-07 §4.2).
  const { active, history } = useMemo(() => {
    const items = (q.data ?? []).filter((p) => p.partCatalog.category !== 'LOADER');
    return {
      active: items.filter((p) => !p.removedAt),
      history: items,
    };
  }, [q.data]);

  // Timeline event 변환.
  const timelineEvents = useMemo<HistoryItem[]>(() => {
    const events: HistoryItem[] = [];
    for (const p of history) {
      // 장착 event
      events.push({
        id: `install-${p.id}`,
        // 추가수정 2차(26-07-27) 정정 — installedAt 은 서버 now() 타임스탬프다.
        // LOT 영향 페이지(PartLotImpactPage)는 이미 KST 로 그리므로 여기만 UTC
        // 자르기면 같은 장착 건이 화면마다 하루 다르게 보인다.
        date: kstYmd(p.installedAt) ?? '-',
        color: lotDefectColor(p),
        title: `[${p.slot}] ${p.partCatalog.displayName}`,
        tag: '장착',
        meta: p.partLot
          ? `LOT ${p.partLot.lotCode}${p.partLot.defectStatus !== 'NORMAL' ? `, ${LOT_DEFECT_LABEL[p.partLot.defectStatus] ?? p.partLot.defectStatus}` : ''}`
          : 'LOT 미기록',
      });
      if (p.removedAt) {
        events.push({
          id: `remove-${p.id}`,
          date: kstYmd(p.removedAt) ?? '-',
          color: 'var(--fg-muted)',
          title: `[${p.slot}] ${p.partCatalog.displayName} 제거`,
          tag: '제거',
          meta: p.removalReason
            ? `사유: ${REMOVAL_REASON_LABEL[p.removalReason] ?? p.removalReason}`
            : '사유 미기록',
        });
      }
    }
    // 최신순 정렬.
    return events.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  }, [history]);

  if (q.isLoading) {
    return <div style={loadingStyle}>불러오는 중…</div>;
  }
  if (q.error) {
    return <div style={errorStyle}>{extractApiError(q.error)}</div>;
  }

  return (
    <div className="rd-stack">
      {/* 2026-10-28 오너 — 왼쪽 부품 목록에서 로봇 그림으로 끌어다 놓아 장착. 장착된 부품은 그림 오른쪽.
          같은 날 "부품 장착 부분은 기본적으로 닫혀있는 상태로" — 아래 막대를 눌러 연다. */}
      <Sheet className="rd-fold">
        <FoldSection
          title="부품 장착"
          summary={`장착된 부품 ${active.length}`}
          defaultOpen={false}
          testId="parts-fold"
        >
          <PartInstallBoard
            robotId={robotId}
            active={active}
            canSwap={canSwapParts}
            onRemove={setRemovingPart}
          />
        </FoldSection>
      </Sheet>

      {/* Phase 2.4 — 적용 변경점(ECN) 정방향 태깅 + 역추적. 부품 LOT recall
          (위 카드의 LOT 링크/defectStatus)과는 직교한 별도 섹션.
          요구1 ⓓ — 게이트는 canTagEcn(서버 robot-ecn WRITE_ROLES 미러).
          2026-10-28 오너 "적용 변경점 부분도 동일한 방식으로" — 부품 장착과 같은 보드.
          같은 날 "적용 변경점은 항상 이렇게 펼쳐져있는 상태로" — 접는 막대 없이 늘 펼쳐 둔다. */}
      <Sheet data-testid="ecn-section">
        <SheetHead title="적용 변경점" count={ecnCount} />
        <div style={{ padding: '0 24px 24px' }}>
          <EcnApplyBoard robotId={robotId} canEdit={canTagEcn} />
        </div>
      </Sheet>

      <Sheet>
        <SheetHead title="교체 이력" count={timelineEvents.length} />
        {timelineEvents.length === 0 ? (
          <SheetEmpty>이력이 없습니다.</SheetEmpty>
        ) : (
          <HistoryList items={timelineEvents} />
        )}
      </Sheet>

      {/* Phase 2.2 — remove modal (v0.4.0 §6.5). 장착 확인 창은 PartInstallBoard 안에 있다. */}
      <PartRemoveModal
        open={removingPart !== null}
        onClose={() => setRemovingPart(null)}
        part={removingPart}
        robotId={robotId}
      />
    </div>
  );
}

function lotDefectColor(p: RobotPartDto): string {
  if (!p.partLot) return 'var(--fg-muted)';
  switch (p.partLot.defectStatus) {
    case 'CONFIRMED':
      return 'var(--severity-urgent)';
    case 'SUSPECTED':
      return 'var(--severity-warning)';
    case 'CLEARED':
      return 'var(--fg-muted)';
    default:
      return 'var(--blue-500)';
  }
}

const loadingStyle: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
};
const errorStyle: CSSProperties = {
  padding: 16,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
  borderRadius: 'var(--r-card)',
};
